import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Shared across every mount so the script is only ever fetched/appended once, even if two
// widgets happened to render at the same time.
let scriptLoadPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptLoadPromise) return scriptLoadPromise;

    scriptLoadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile script')));
            return;
        }
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Turnstile script'));
        document.head.appendChild(script);
    });
    return scriptLoadPromise;
}

/**
 * Cloudflare Turnstile challenge widget. Only ever mounted on the public sign-up form - the
 * verification it produces a token for is enforced server-side, so this component's only job
 * is collecting that token via onVerify and clearing it via onExpire/onError so a stale or
 * failed challenge can never be submitted as if it were valid.
 *
 * Exposes a `reset()` method via ref so the caller can request a fresh token after a failed
 * registration attempt (Turnstile tokens are single-use).
 */
const TurnstileWidget = forwardRef(function TurnstileWidget({ siteKey, onVerify, onExpire, onError }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const elementId = useId();

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (window.turnstile && widgetIdRef.current != null) {
                window.turnstile.reset(widgetIdRef.current);
            }
        },
    }));

    useEffect(() => {
        if (!siteKey || !containerRef.current) return undefined;
        let cancelled = false;

        loadTurnstileScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) return;
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: onVerify,
                    'expired-callback': onExpire,
                    'error-callback': onError,
                });
            })
            .catch((err) => {
                console.error('[Turnstile] failed to load challenge script:', err);
                if (onError) onError(err);
            });

        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current != null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
        // Re-render only if the site key itself changes; the callbacks are intentionally not
        // in the dependency array so re-renders from typing in the form don't tear down and
        // recreate the widget (which would discard an already-verified token).
        // eslint-disable-next-line
    }, [siteKey]);

    if (!siteKey) {
        // Fails closed on the backend regardless, but surface the misconfiguration in dev.
        if (process.env.NODE_ENV !== 'production') {
            console.warn('[Turnstile] REACT_APP_TURNSTILE_SITE_KEY is not set; the widget will not render.');
        }
        return null;
    }

    return <div ref={containerRef} id={`turnstile-widget-${elementId}`} />;
});

export default TurnstileWidget;
